import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { BiChevronLeft, BiChevronRight, BiPlay, BiPause } from "react-icons/bi";

// Fixed waveform pattern - same for all items
const waveformHeights = [
    30, 60, 45, 75, 50, 85, 40, 70, 55, 90, 35, 65, 48, 80, 42, 72, 58, 88, 38,
    68, 52, 78, 44, 82, 36, 74, 60, 86, 46, 76,
];

export default function AudioCarousel({
    title = "AUDIO",
    items = [],
    cardWidth = "250px",
    cardHeight = "auto",
    imageHeight = "250px",
    gap = "gap-4",
    className = "",
    titleClassName = "text-2xl font-bold text-primary mb-6",
    showArrows = true,
    scrollAmount = 300,
}) {
    const scrollContainerRef = useRef(null);
    const [showLeftArrow, setShowLeftArrow] = useState(false);
    const [showRightArrow, setShowRightArrow] = useState(true);
    const [playingId, setPlayingId] = useState(null);
    const [isPlay, setIsPlay] = useState(false);
    const [urlAudio, setUrlAudio] = useState(null);
    const audioRef = useRef();

    useEffect(() => {
        if (!audioRef.current || !urlAudio) return;

        audioRef.current.load();

        if (isPlay) {
            audioRef.current
                .play()
                .catch((err) => console.log("Audio play blocked:", err));
        }
    }, [urlAudio, isPlay]);

    const handleScroll = () => {
        if (scrollContainerRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } =
                scrollContainerRef.current;
            setShowLeftArrow(scrollLeft > 0);
            setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
        }
    };

    const scroll = (direction) => {
        if (scrollContainerRef.current) {
            const scrollValue =
                direction === "left" ? -scrollAmount : scrollAmount;
            scrollContainerRef.current.scrollBy({
                left: scrollValue,
                behavior: "smooth",
            });
        }
    };

    const playAudio = (id) => {
        const item = items.find((i) => i.id === id);
        if (!item?.audioUrl) return;

        if (playingId === id && isPlay) {
            audioRef.current.pause();
            setIsPlay(false);
            return;
        }

        setPlayingId(id);
        setUrlAudio(item.audioUrl);
        setIsPlay(true);
    };

    const AudioCard = ({ item }) => {
        const isPlaying = playingId === item.id;

        return (
            <div className="flex-shrink-0 group" style={{ width: cardWidth }}>
                <div className="bg-primary/5 rounded-lg border-2 border-primary hover:shadow-lg transition-shadow p-3">
                    {/* Cover Image */}
                    <Link href={item.link}>
                        <div className="relative aspect-square rounded-lg overflow-hidden">
                            <Image
                                src={item.coverImage}
                                alt={item.title}
                                fill
                                className="object-cover"
                            />
                        </div>
                    </Link>

                    {/* Title, Play Button and Waveform */}
                    <div className="mt-3">
                        <Link href={item.link}>
                            <h3 className="text-sm font-medium text-gray-800 line-clamp-2 mb-2 group-hover:text-primary transition-colors leading-snug">
                                {item.title}
                            </h3>
                        </Link>

                        {/* Play Button and Waveform */}
                        <div className="flex items-center gap-2">
                            <button
                                onClick={(e) => {
                                    e.preventDefault();
                                    playAudio(item.id);
                                }}
                                className="flex-shrink-0 w-8 h-8 rounded-full bg-primary hover:bg-primary/90 flex items-center justify-center transition-all"
                            >
                                {isPlaying ? (
                                    <BiPause className="text-white text-lg" />
                                ) : (
                                    <BiPlay className="text-white text-lg ml-0.5" />
                                )}
                            </button>

                            {/* Waveform visualization */}
                            <div className="flex-1 flex items-center gap-0.5 h-6">
                                {waveformHeights.map((height, i) => (
                                    <div
                                        key={i}
                                        className={`flex-1 rounded-full transition-all ${
                                            isPlaying
                                                ? "bg-primary"
                                                : "bg-gray-300"
                                        }`}
                                        style={{
                                            height: `${height}%`,
                                            minHeight: "2px",
                                        }}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className={`w-full ${className}`}>
            {/* Title */}
            <h2 className={titleClassName}>{title}</h2>

            {/* Slider Container with Arrows */}
            <div className="relative group/slider">
                {/* Left Arrow */}
                {showArrows && showLeftArrow && (
                    <button
                        onClick={() => scroll("left")}
                        className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white shadow-lg rounded-full p-3 opacity-0 group-hover/slider:opacity-100 transition-opacity duration-300"
                        aria-label="Scroll left"
                    >
                        <BiChevronLeft className="text-3xl text-gray-700" />
                    </button>
                )}

                {/* Right Arrow */}
                {showArrows && showRightArrow && (
                    <button
                        onClick={() => scroll("right")}
                        className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white shadow-lg rounded-full p-3 opacity-0 group-hover/slider:opacity-100 transition-opacity duration-300"
                        aria-label="Scroll right"
                    >
                        <BiChevronRight className="text-3xl text-gray-700" />
                    </button>
                )}

                {/* Horizontal Scrollable Cards */}
                <div
                    ref={scrollContainerRef}
                    onScroll={handleScroll}
                    className="overflow-x-auto overflow-y-hidden scrollbar-hide"
                >
                    <div className={`flex ${gap} pb-4`}>
                        {items.map((item) => (
                            <AudioCard key={item.id} item={item} />
                        ))}
                    </div>
                </div>
            </div>
            <audio ref={audioRef} src={urlAudio} preload="auto" />

            <style jsx>{`
                .scrollbar-hide::-webkit-scrollbar {
                    display: none;
                }
                .scrollbar-hide {
                    -ms-overflow-style: none;
                    scrollbar-width: none;
                }
            `}</style>
        </div>
    );
}
